import { useApolloClient } from '@apollo/client';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { SchemaOf, ValidationError } from 'yup';
import {
    FIELD_LABELS,
    ORDER_REQUEST_FIELD_LABELS,
    getDateFromString,
    getStringFromDate,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import {
    ActivityEntityName,
    queryIsOrderQuotationCodeOccupied,
    useUpsertOrderQuotationMutation,
} from '../../../../../../services';
import { useHistorialTab } from '../../../../historial';
import {
    DAuditInfo,
    IAuditStamps,
    RhCheckbox,
    RhDateInput,
    RhFormWrapper,
    RhTabsWrapper,
    RHTextField,
} from '../../../../../dum';
import { AccountInput } from './inputs/account/account-input';
import {
    DEFAULT_ORDER_QUOTATION_PRODUCT,
    OrderQuotationProductsColumns,
} from './inputs/order-quotation-products/order-quotation-products-columns';
import { OrderCodeInput } from './inputs/order_code/order-code-input';
import { QuotationTotals } from './inputs/totals/quotation-totals';
import { isProductUnique } from './validation/is-product-unique';
import { isKiloPriceAndGroupPriceDifferentThanZero } from './validation/is-kilo-price-and-group-price-different-than-zero';
import { isProposedDescriptionRequired } from './validation/is-proposed-description-required';

export interface IOrderQuotation {
    id?: number | null;
    date?: string | null;
    order_code: number;
    account_id?: number | null;
    products_total: number;
    estimated_delivery_date?: string | null;
    notes?: string;
    // Cotización-specific header fields. vigencia (NULL = sin vencimiento),
    // condiciones de pago (free text), and whether IVA shows on the document.
    expiration_date?: string | null;
    payment_terms?: string;
    require_tax: boolean;
    order_quotation_products: {
        id?: number | null;
        kilos: number;
        kilo_price: number;
        group_price: number;
        // NULL for a free line. When set, group_weight is filled from the
        // product and locked; the price stays free (no catalog check).
        product?: {
            id: number;
            compound_description: string;
            current_group_weight?: number | null;
        } | null;
        // Used only when product is null (free line): the specification text.
        proposed_description?: string;
        groups: number;
        group_weight?: number | null;
    }[];
}

// Audit stamps ride alongside the form values — display only, never submitted,
// and deliberately kept out of IOrderQuotation (see IAuditStamps).
interface TOrderQuotation extends IOrderQuotation, IAuditStamps {
    id?: number | null;
}

interface ISOrderQuotationFormDialogProps {
    onSuccess: () => void;
    orderQuotation?: TOrderQuotation;
    disabled?: boolean;
}

export const ID: keyof IOrderQuotation = 'id';

export const OrderQuotationForm = (props: ISOrderQuotationFormDialogProps) => {
    const { orderQuotation, onSuccess, disabled = false } = props;

    const apolloClient = useApolloClient();

    const [mutation, { loading }] = useUpsertOrderQuotationMutation();

    const historialTab = useHistorialTab({
        entityName: ActivityEntityName.OrderQuotation,
        entityId: orderQuotation?.id,
        unsavedMessage:
            'El historial aparece una vez que la cotización ha sido guardada.',
        emptyMessage: 'No hay actividades registradas para esta cotización.',
    });

    const initialValues = useMemo<IOrderQuotation>(() => {
        return {
            id: orderQuotation?.id || undefined,
            order_code: orderQuotation?.order_code || 0,
            notes: orderQuotation?.notes || '',
            date: orderQuotation?.date
                ? getDateFromString(orderQuotation.date)
                : null,
            estimated_delivery_date: orderQuotation?.estimated_delivery_date
                ? getDateFromString(orderQuotation.estimated_delivery_date)
                : null,
            expiration_date: orderQuotation?.expiration_date
                ? getDateFromString(orderQuotation.expiration_date)
                : null,
            payment_terms: orderQuotation?.payment_terms || '',
            require_tax: orderQuotation?.require_tax || false,
            products_total: orderQuotation?.products_total
                ? orderQuotation.products_total
                : 0,
            account_id: orderQuotation?.account_id
                ? orderQuotation.account_id
                : null,
            order_quotation_products: orderQuotation
                ? orderQuotation.order_quotation_products || []
                : [{ ...DEFAULT_ORDER_QUOTATION_PRODUCT }],
        };
    }, [orderQuotation]);

    const validationSchema: SchemaOf<IOrderQuotation> = yup.object({
        id: yup.number().notRequired().nullable(),
        order_code: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(`'Folio' es obligatorio`)
            .test('unique', async function (value, context): Promise<
                boolean | ValidationError
            > {
                if (!!value) {
                    const result = await queryIsOrderQuotationCodeOccupied(
                        apolloClient,
                        {
                            OrderCode: value,
                            OrderQuotationId: orderQuotation?.id || null,
                        },
                    );
                    if (result.data.isOrderQuotationCodeOccupied) {
                        return context.createError({
                            message: `El folio ${value} ya está en uso`,
                        });
                    }
                    return true;
                } else {
                    return true;
                }
            }),
        date: yup.string().required(`'Fecha' es obligatorio`).nullable(),
        estimated_delivery_date: yup.string().notRequired().nullable(),
        // vigencia: optional. Blank means "sin vencimiento".
        expiration_date: yup.string().notRequired().nullable(),
        payment_terms: yup.string(),
        require_tax: yup.boolean().required(),
        products_total: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(`'Total de productos' es obligatorio`),
        account_id: yup
            .number()
            .transform(transformToNumber)
            .nullable()
            .required(`'Cliente' es obligatorio`),
        notes: yup.string(),
        order_quotation_products: yup
            .array()
            .min(1, `'Productos' es obligatorio`)
            .of(
                yup.object().shape({
                    id: yup.number().notRequired().nullable(),
                    // NOT required — a free line has no product. Uniqueness is
                    // only enforced among linked lines (two free lines are not
                    // duplicates); there is deliberately no catalog check.
                    product: yup
                        .mixed()
                        .notRequired()
                        .nullable()
                        .test('is-product-unique', isProductUnique),
                    // Required only when there is no product (free line).
                    proposed_description: yup
                        .string()
                        .test(
                            'is-proposed-description-required',
                            isProposedDescriptionRequired,
                        ),
                    kilos: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .min(0, `'Kilos' no puede ser menor a 0`)
                        .required(`'Kilos' es obligatorio`),
                    kilo_price: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Precio por kilo' es requerido`)
                        .test(
                            'is-kilo-price-and-group-price-different-than-zero',
                            isKiloPriceAndGroupPriceDifferentThanZero,
                        ),
                    group_price: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .required(`'Precio por grupo' es requerido`)
                        .test(
                            'is-kilo-price-and-group-price-different-than-zero',
                            isKiloPriceAndGroupPriceDifferentThanZero,
                        ),
                    groups: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .min(0, `'Grupos' no puede ser menor a 0`)
                        .required(`'Grupos' es obligatorio`),
                    group_weight: yup
                        .number()
                        .transform(transformToNumber)
                        .nullable()
                        .min(0, `'Peso' no puede ser menor a 0`)
                        .required('`Peso` es obligatiorio')
                        .nullable(),
                }),
            ),
    });

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            actionBarStart={
                orderQuotation?.id ? (
                    <DAuditInfo
                        created_by={orderQuotation.created_by}
                        updated_by={orderQuotation.updated_by}
                        createdLabel={'Creado por'}
                        updatedLabel={'Actualizado por'}
                    />
                ) : null
            }
            onSubmit={async (data) => {
                try {
                    await mutation({
                        variables: {
                            OrderQuotationInput: {
                                id: orderQuotation?.id || null,
                                date: data.date
                                    ? getStringFromDate(data.date)
                                    : null,
                                estimated_delivery_date:
                                    data.estimated_delivery_date
                                        ? getStringFromDate(
                                              data.estimated_delivery_date,
                                          )
                                        : null,
                                expiration_date: data.expiration_date
                                    ? getStringFromDate(data.expiration_date)
                                    : null,
                                payment_terms: data.payment_terms || '',
                                require_tax: data.require_tax,
                                // Status is not sent: the backend defaults new
                                // quotations to Borrador (id = 1) and only the
                                // admin-only updateOrderQuotationStatus /
                                // acceptOrderQuotation move it.
                                notes: data.notes || '',
                                account_id: data.account_id!,
                                order_code: data.order_code,
                                order_quotation_products:
                                    data.order_quotation_products.map(
                                        (quotationProduct) => {
                                            return {
                                                id: quotationProduct.id || null,
                                                kilos: quotationProduct.kilos,
                                                kilo_price:
                                                    quotationProduct.kilo_price,
                                                // NULL on a free line.
                                                product_id:
                                                    quotationProduct.product
                                                        ?.id ?? null,
                                                // Only carries text on a free
                                                // line; a linked line renders the
                                                // product's description instead.
                                                proposed_description:
                                                    quotationProduct.product
                                                        ? ''
                                                        : quotationProduct.proposed_description ||
                                                          '',
                                                group_weight:
                                                    quotationProduct.group_weight!,
                                                groups: quotationProduct.groups,
                                                group_price:
                                                    quotationProduct.group_price,
                                            };
                                        },
                                    ),
                            },
                        },
                    });
                    onSuccess();
                } catch (e) {
                    console.error(e);
                }
            }}
            onInvalid={async (errors) => {
                console.log(errors);
            }}
        >
            <RhTabsWrapper
                tabs={[
                    {
                        label: 'General',
                        fields: [
                            'date',
                            'order_code',
                            'account_id',
                            'expiration_date',
                            'payment_terms',
                            'require_tax',
                        ],
                        content: (
                            <>
                                <RhDateInput
                                    name={'date'}
                                    label={shortLabel(
                                        ORDER_REQUEST_FIELD_LABELS.date,
                                    )}
                                />
                                <OrderCodeInput />
                                <AccountInput />
                                <RhDateInput
                                    name={'expiration_date'}
                                    label={shortLabel(
                                        FIELD_LABELS.expiration_date,
                                    )}
                                />
                                <RHTextField
                                    name={'payment_terms'}
                                    label={shortLabel(
                                        FIELD_LABELS.payment_terms,
                                    )}
                                    rows={2}
                                />
                                <RhCheckbox
                                    name={'require_tax'}
                                    label={'IVA visible'}
                                />
                            </>
                        ),
                    },
                    {
                        label: 'Productos',
                        fields: ['order_quotation_products', 'products_total'],
                        content: (
                            <>
                                <OrderQuotationProductsColumns />
                                <QuotationTotals />
                            </>
                        ),
                    },
                    {
                        label: 'Datos Opcionales',
                        optional: true,
                        fields: ['estimated_delivery_date', 'notes'],
                        content: (
                            <>
                                <RhDateInput
                                    name={'estimated_delivery_date'}
                                    label={shortLabel(
                                        FIELD_LABELS.estimated_delivery_date,
                                    )}
                                />
                                <RHTextField
                                    name={'notes'}
                                    label={shortLabel(FIELD_LABELS.notes)}
                                    rows={3}
                                />
                            </>
                        ),
                    },
                    // Empty for non-admins, so they get no tab at all.
                    ...historialTab,
                ]}
            />
        </RhFormWrapper>
    );
};
